import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

type BadgeVariant = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  className?: string;
}

const variants: Record<BadgeVariant, string> = {
  primary: 'bg-primary/20 text-primary',
  success: 'bg-success/20 text-success',
  warning: 'bg-warning/20 text-warning',
  error: 'bg-error/20 text-error',
  info: 'bg-info/20 text-info',
  neutral: 'bg-secondary text-foreground-muted',
};

export function Badge({ children, variant = 'neutral', className }: BadgeProps) {
  return (
    <span className={cn('badge', variants[variant], className)}>
      {children}
    </span>
  );
}

// Status-specific badges
export function StatusBadge({ status }: { status: string }) {
  const statusMap: Record<string, { variant: BadgeVariant; label: string }> = {
    available: { variant: 'success', label: 'Available' },
    checked_out: { variant: 'warning', label: 'Checked Out' },
    worn: { variant: 'error', label: 'Worn' },
    damaged: { variant: 'error', label: 'Damaged' },
    discarded: { variant: 'neutral', label: 'Discarded' },
    active: { variant: 'success', label: 'Active' },
    completed: { variant: 'info', label: 'Completed' },
    cancelled: { variant: 'neutral', label: 'Cancelled' },
    in_progress: { variant: 'warning', label: 'In Progress' },
    pending: { variant: 'neutral', label: 'Pending' },
    processing: { variant: 'warning', label: 'Processing' },
    failed: { variant: 'error', label: 'Failed' },
  };

  const config = statusMap[status] || { variant: 'neutral', label: status };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export function RoleBadge({ role }: { role: string }) {
  const roleMap: Record<string, { variant: BadgeVariant; label: string }> = {
    admin: { variant: 'error', label: 'Admin' },
    manager: { variant: 'warning', label: 'Manager' },
    user: { variant: 'primary', label: 'User' },
    viewer: { variant: 'neutral', label: 'Viewer' },
  };

  const config = roleMap[role] || { variant: 'neutral', label: role };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}
